import {
  AppBar,
  Box,
  CssBaseline,
  Drawer as MuiDrawer,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Toolbar,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
  Divider,
  Badge,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import MenuIcon from "@mui/icons-material/Menu";
import {
  DashboardOutlined,
  UserAddOutlined
} from "@ant-design/icons";
import {
  EventNote,
  EmojiEvents,
  EventAvailable,
  MenuOpen,
  NotificationsNone,
  FullscreenExit,
  Fullscreen,
  ExpandLess,
  ExpandMore,
  FiberManualRecord,
  ConfirmationNumber,
  AssignmentTurnedIn,
  ForumOutlined,
  AccountCircle
} from "@mui/icons-material";
import React, { useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import Dialogbox from "../Components/Dialogbox";
import { CustomIcons } from "src/Assets/Icons/Icons";
import text from "../Assets/Text/Text.json";
import man from "../Assets/Images/man.png";
import logo from "../Assets/Images/va_white_trans.png";
import "../Common/css/navbar.css";
import { getNotificationCount } from "src/Services/notification_api";
import { chatApi } from "src/Services/chat_api";
import { getCommunityUnreadCount } from "src/Services/com_chat_api";
import { tokenCheck } from "src/Services/auth_api";

const drawerWidth = 200;
const collapsedWidth = 70;

interface Props {
  children: React.ReactElement;
}

const Drawer = styled(MuiDrawer)(({ theme }) => ({
  '& .MuiDrawer-paper': {
    width: drawerWidth,
    backgroundColor: "#1e1e1e",
    color: "#fff",
    borderRight: "1px solid #333",
  },
}));

export default function Navbar({ children }: Readonly<Props>) {
  const [mobileOpen, setMobileOpen] = React.useState(false);
  const [openDrawer, setOpenDrawer] = React.useState(true); // for desktop toggle
  const [isOpen, setIsOpen] = React.useState(false);
  const [paymentModalOpen, setPaymentModalOpen] = React.useState(false);
  const [lockedModalOpen, setLockedModalOpen] = React.useState(false);
  const [isFullscreen, setIsFullscreen] = React.useState(false);
  const [openSubmenus, setOpenSubmenus] = React.useState<Record<string, boolean>>({});
  const [notificationCount, setNotificationCount] = React.useState<any>(null);
  const [conversations, setConversations] = React.useState<any[]>([]);
  const [communityUnreadCount, setCommunityUnreadCount] = React.useState<number>(0);

  const userType = sessionStorage.getItem("userType") || "";

  const handleToggleFullscreen = () => {
    if (!document.fullscreenElement) {
      document.documentElement.requestFullscreen();
      setIsFullscreen(true);
    } else {
      document.exitFullscreen();
      setIsFullscreen(false);
    }
  };
  const toggleSubmenu = (menuName: string) => {
    setOpenSubmenus((prev) => ({
      ...prev,
      [menuName]: !prev[menuName],
    }));
  };


  const theme = useTheme();
  const isMobile = useMediaQuery("(max-width:768px)");
  const fullScreen = useMediaQuery(theme.breakpoints.down("sm"));
  const token = sessionStorage.getItem("token") || "";
  const userId = sessionStorage.getItem("userId") || "";
  const user = sessionStorage.getItem("username") || "User";
  const expireRaw = sessionStorage.getItem("expireDate") || "No";
  const dueAmount = sessionStorage.getItem("dueAmount") || "0.00";
  const dueInstallments = sessionStorage.getItem("dueInstallments") || "0";
  const navigate = useNavigate();
  const location = useLocation();
  
  React.useEffect(() => {
    if (!token) navigate("/");
  }, [token]);

  // open appropriate modal based on expireDate value
  useEffect(() => {
    if (!token) return;

    if (expireRaw === "No") {
      setPaymentModalOpen(true);
      return;
    }

    // try to parse a date from the stored string (be permissive)
    const parseDateFromString = (s: string): Date | null => {
      if (!s) return null;
      // direct parse (ISO or timestamp)
      const direct = Date.parse(s);
      if (!isNaN(direct)) return new Date(direct);

      // try splitting by common separators and parse each token
      const parts = s.split(/[\|,; ]+/);
      for (const p of parts) {
        const pVal = p.trim();
        if (!pVal) continue;
        const pParsed = Date.parse(pVal);
        if (!isNaN(pParsed)) return new Date(pParsed);
        // try dd/mm/yyyy or dd-mm-yyyy
        const m = pVal.match(/^(\d{1,2})[\/\-](\d{1,2})[\/\-](\d{2,4})$/);
        if (m) {
          let day = parseInt(m[1], 10);
          let month = parseInt(m[2], 10);
          let year = parseInt(m[3], 10);
          if (year < 100) year += 2000;
          return new Date(year, month - 1, day);
        }
      }
      return null;
    };

    const expDate = parseDateFromString(expireRaw);
    if (!expDate) return;

    const today = new Date();
    today.setHours(0, 0, 0, 0);
    expDate.setHours(0, 0, 0, 0);

    // if expiry is before today => locked
    if (expDate < today && (parseFloat(dueAmount) > 0)) {
      setLockedModalOpen(true);
    }
  }, [expireRaw, token]);
  
  const handleClosePaymentModal = () => {
    setPaymentModalOpen(false);
    // requirement: closing the modal should call logout
    handleLogout();
  };

  const handleCloseLockedModal = () => {
    setLockedModalOpen(false);
    handleLogout();
  };
  
  interface DrawerItem {
    name: string;
    icon: React.ReactNode;
    onClick?: () => void;
    active?: boolean;
    children?: DrawerItem[];
  }

  const path = location.pathname;
  const drawerItems: DrawerItem[] = [
    {
      name: "Dashboard",
      icon: <DashboardOutlined style={{ fontSize: 20 }} />,
      onClick: () => navigate("/dashboard"),
      active: path === "/dashboard"
    },
    {
      name: "Courses",
      icon: <EventNote fontSize="small" />,
      onClick: () => navigate("/courses"),
      active: path === "/courses"
    },
    {
      name: "Tickets",
      icon: <ConfirmationNumber fontSize="small" />,
      onClick: () => navigate("/tickets"),
      active: path === "/tickets"
    },
    {
      name: "Attendance",
      icon: <EventAvailable fontSize="small" />,
      onClick: () => navigate("/attendance"),
      active: path === "/attendance"
    },
    {
      name: "Marks",
      icon: <AssignmentTurnedIn fontSize="small" />,
      onClick: () => navigate("/marks"),
      active: path === "/marks"
    },
    {
      name: "Chats",
      icon: (
        <Badge
          badgeContent={conversations.reduce((sum, c) => sum + (c.unreadCount || 0), 0) + communityUnreadCount}
          color="error"
          overlap="rectangular"
          invisible={conversations.reduce((sum, c) => sum + (c.unreadCount || 0), 0) + communityUnreadCount === 0}
        >
          <ForumOutlined fontSize="small" />
        </Badge>
      ),
      onClick: () => navigate("/chats"),
      active: path === "/chats"
    },
    {
      name: "Profile",
      icon: <AccountCircle fontSize="small" />,
      onClick: () => navigate("/profile"),
      active: path === "/profile"
    }
  ];

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleDesktopDrawerToggle = () => {
    setOpenDrawer(prev => !prev);
  };

  const handleLogout = () => {
    sessionStorage.clear();
    navigate("/");
  };

  const handleGetUnreadNotificationsCount = async () => {
    try {
      const res = await getNotificationCount(userId, token);
      setNotificationCount(res);
    } catch (error) {
      console.error(error);
    }
  }

  const handleGetCommunityUnreadCount = async () => {
    try {
      const res = await getCommunityUnreadCount(token, userId, "1");
      setCommunityUnreadCount(res.data.unreadCount|| 0);
    } catch (error) {
      console.error(error);
    }
  }

  useEffect(() => {
    handleGetUnreadNotificationsCount();
    handleGetConversations();
    handleGetCommunityUnreadCount();
    handleTokenCheck()
  }, []);

  let timeout: any = null;

  useEffect(() => {
    const handleClick = () => {
      clearTimeout(timeout);
      timeout = setTimeout(() => {
        handleGetUnreadNotificationsCount();
        handleGetConversations();
        handleGetCommunityUnreadCount();
        handleTokenCheck();
      }, 1000);
    };

    window.addEventListener("click", handleClick);

    return () => {
      window.removeEventListener("click", handleClick);
    };
  }, []);

  useEffect(() => {
    const handleMessagesRead = () => {
      handleGetCommunityUnreadCount();
    };

    window.addEventListener("communityMessagesRead", handleMessagesRead);

    return () => {
      window.removeEventListener("communityMessagesRead", handleMessagesRead);
    };
  }, [handleGetCommunityUnreadCount]);

  const handleGetConversations = async () => {
    try {
      const res = await chatApi.getConversations(Number(userId), 1);
      setConversations(res);
    } catch (error) {
      console.error(error);
    }
  }

  const handleTokenCheck = async () => {
    try {
      await tokenCheck(token)
    } catch (error:any) {
      if(error.status === 401){
        handleLogout();
      }      
    }
  }

  const drawerContent = (
    <Box sx={{ height: "100%", display: "flex", flexDirection: "column", justifyContent: "space-between", marginTop: isMobile ? "60px" : 0 }}>
      <List>
        {drawerItems.map((item, index) => {
          const hasChildren = !!item.children?.length;
          const isExpanded = openSubmenus[item.name];

          return (
            <React.Fragment key={index}>
              <ListItem disablePadding sx={{ display: "block" }}>
                <ListItemButton
                  onClick={() => {
                    if (hasChildren) {
                      toggleSubmenu(item.name);
                    } else {
                      if (item.onClick) {
                        item.onClick();
                      }
                    }
                  }}
                  sx={{
                    justifyContent: openDrawer ? "initial" : "center",
                    px: 2,
                    py: 1,
                    m: 0.5,
                    borderRadius: 2,
                    color: item.active ? "#FFEC00" : "#ccc",
                    backgroundColor: item.active ? "#2a2a2a" : "transparent",
                    "&:hover": { backgroundColor: "#333" },
                  }}
                >
                  <ListItemIcon
                    sx={{
                      color: "inherit",
                      minWidth: 0,
                      mr: openDrawer ? 1.5 : "auto",
                      justifyContent: "center",
                      fontSize: "1.2rem",
                    }}
                  >
                    {item.icon}
                  </ListItemIcon>

                  {openDrawer && (
                    <>
                      <ListItemText
                        primary={item.name}
                        primaryTypographyProps={{ fontSize: "13px" }}
                      />
                      {hasChildren && (
                        isExpanded ? (
                          <ExpandLess sx={{ fontSize: 16, color: "#ccc" }} />
                        ) : (
                          <ExpandMore sx={{ fontSize: 16, color: "#ccc" }} />
                        )
                      )}
                    </>
                  )}
                </ListItemButton>
              </ListItem>

              {/* Show subitems only if expanded */}
              {hasChildren && isExpanded && (item.children ?? []).map((child, cIndex) => (
                <Box
                  key={cIndex}
                  sx={{
                    position: "relative",
                    display: openDrawer ? "flex" : "none",
                    alignItems: "center",
                    ml: openDrawer ? 1.5 : 0,
                    pl: openDrawer ? 3 : 0,
                    '&::before': {
                      content: '""',
                      position: "absolute",
                      top: "20%",
                      left: openDrawer ? 15 : 0,
                      width: 32,
                      height: 50,
                      borderLeft: "1px solid rgb(255, 255, 255)",
                      borderBottom: "2px solid rgb(244, 243, 238)",
                      borderBottomLeftRadius: "6px",
                      transform: "translateY(-50%)",
                    },
                  }}
                >
                  <Box
                    sx={{
                      position: "relative",
                      pl: 2,
                      '&::before': {
                        content: '""',
                        position: 'absolute',
                        left: 0,
                        top: "50%",
                        width: "12px",
                        height: "1px",
                        // bgcolor: "#555",
                        transform: "translateY(-50%)",
                      },
                    }}
                  >
                    <ListItem disablePadding>
                      <ListItemButton
                        onClick={child.onClick}
                        sx={{
                          justifyContent: openDrawer ? "initial" : "center",
                          px: 2,
                          py: 0.5,
                          m: 0.5,
                          borderRadius: 2,
                          border: child.active ? "1px solid rgb(244, 243, 238)" : "1px solid transparent",
                          color: child.active ? "#FFEC00" : "#aaa",
                          // backgroundColor: child.active ? "#2a2a2a" : "transparent",
                          background: child.active ? "linear-gradient(180deg,rgb(7, 7, 7) 0%, rgb(0, 0, 0) 50%,rgb(58, 58, 58) 100%) !important" : "transparent",
                          "&:hover": { backgroundColor: "#333" },
                        }}
                      >
                        {openDrawer && (
                          <ListItemText primary={child.name} primaryTypographyProps={{ fontSize: "12px" }} />
                        )}
                      </ListItemButton>
                    </ListItem>
                  </Box>
                </Box>

              ))}
            </React.Fragment>
          );
        })}
      </List>

      <ListItem disablePadding sx={{ display: "block", mb: 1 }}>
        <ListItemButton
          onClick={() => setIsOpen(true)}
          sx={{
            justifyContent: openDrawer ? "initial" : "center",
            px: 2,
            py: 1,
            m: 0.5,
            borderRadius: 2,
            color: "#ccc",
            "&:hover": { backgroundColor: "#333" },
          }}
        >
          <ListItemIcon
            sx={{
              color: "inherit",
              minWidth: 0,
              mr: openDrawer ? 1.5 : "auto",
              justifyContent: "center",
              fontSize: "1.2rem",
            }}
          >
            {CustomIcons.Logout}
          </ListItemIcon>
          {openDrawer && (
            <ListItemText
              primary={text.navigation.logout}
              primaryTypographyProps={{ fontSize: "13px" }}
            />
          )}
        </ListItemButton>
      </ListItem>

    </Box>
  );

  return (
    <Box sx={{ display: "flex", height: "100vh" }}>
      <CssBaseline />
      {/* App Bar */}
      <AppBar
        position="fixed"
        sx={{
          bgcolor: "#1e1e1e",
          width: isMobile ? "100%" : `calc(100% - ${openDrawer ? drawerWidth : collapsedWidth}px)`,
          ml: isMobile ? 0 : `${openDrawer ? drawerWidth : collapsedWidth}px`,
          zIndex: theme.zIndex.drawer + 1,
          borderBottom: "1px solid #9d9d9b61",
        }}
      >
        <Toolbar
          sx={{
            minHeight: "64px",
            justifyContent: "space-between",
            px: 2,
            maxWidth: "100%",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, border: "1px solid #9d9d9b61", borderRadius: "4px", padding: "0px 8px", backgroundColor: "#252525" }}>
            <IconButton color="inherit" onClick={isMobile ? handleDrawerToggle : handleDesktopDrawerToggle}>
              <MenuOpen style={{ fontSize: isMobile ? "16px" : "20px" }} />
            </IconButton>
            <Typography
              variant="h6"
              noWrap
              sx={{
                display: "flex",
                fontSize: isMobile ? 12 : 13,
                fontWeight: 500,
                color: "#fff",
              }}
            >
              {userType.toUpperCase()} PORTAL
            </Typography>
          </Box>

          <Box sx={{ display: "flex", alignItems: "center", gap: isMobile ? 0.5 : 1.5 }}>
            <Divider orientation="vertical" flexItem sx={{ borderColor: "#9d9d9b61", mx: isMobile ? 0.5 : 1 }} />
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, border: "1px solid #9d9d9b61", borderRadius: "30px", backgroundColor: "#252525" }}>
              <Tooltip title={isFullscreen ? "Exit Fullscreen" : "Enter Fullscreen"}>
                <IconButton sx={{ color: "#ccc", width: isMobile ? 30 : 35, height: isMobile ? 30 : 35 }} onClick={handleToggleFullscreen}>
                  {isFullscreen ? (
                    <FullscreenExit fontSize="small" />
                  ) : (
                    <Fullscreen fontSize="small" />
                  )}
                </IconButton>
              </Tooltip>
            </Box>

            <Box sx={{ display: "flex", alignItems: "center", gap: 1, border: "1px solid #9d9d9b61", borderRadius: "30px", backgroundColor: "#252525" }}>
              <Tooltip title="Notifications">
                <IconButton
                  sx={{ color: "#ccc", width: isMobile ? 30 : 35, height: isMobile ? 30 : 35 }}
                  onClick={() => navigate("/notifications")}
                >
                  <Badge
                    badgeContent={notificationCount?.unreadCount || 0}
                    color="error"
                    overlap="rectangular"
                    invisible={notificationCount?.unreadCount === 0}
                  >
                    <NotificationsNone fontSize="small" />
                  </Badge>
                </IconButton>
              </Tooltip>

            </Box>



            <Tooltip title={`Logged in as ${user}`}>
              <Box
                sx={{
                  cursor: "pointer",
                  bgcolor: "#aaa",
                  borderRadius: "50%",
                  width: isMobile ? 30 : 35,
                  height: isMobile ? 30 : 35,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  "&:hover": { bgcolor: "#bbb" },
                }}
              >
                <img src={man} alt="user" style={{ width: isMobile ? "20px" : "26px" }} />
              </Box>
            </Tooltip>
          </Box>

        </Toolbar>
      </AppBar>


      {/* Drawer */}
      {isMobile ? (
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{ keepMounted: true }}
        >
          <Toolbar /> {/* Offset space for AppBar */}
          {drawerContent}
        </Drawer>

      ) : (
        <Drawer
          variant="permanent"
          open
          sx={{
            width: openDrawer ? drawerWidth : collapsedWidth,
            flexShrink: 0,
            '& .MuiDrawer-paper': {
              width: openDrawer ? drawerWidth : collapsedWidth,
              backgroundColor: "#1e1e1e",
              borderRight: "1px solid #9d9d9b61",
              color: "#fff",
              transition: theme.transitions.create('width', {
                easing: theme.transitions.easing.sharp,
                duration: theme.transitions.duration.enteringScreen,
              }),
              overflowX: 'hidden',
            },
          }}
        >
          <Toolbar />
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: openDrawer ? "flex-start" : "center",
              px: openDrawer ? 2 : 0,
              py: 2,
              height: 64,
            }}
          >
            <img
              src={logo}
              alt="Logo"
              style={{ width: 30, height: "auto" }}
            />
            {openDrawer && (
              <Typography
                variant="h6"
                noWrap
                sx={{
                  ml: 1.5,
                  fontSize: 16,
                  fontWeight: 600,
                  color: "#fff",
                }}
              >
                VANGUARD
              </Typography>
            )}
          </Box>
          <Divider sx={{ borderColor: "#333", mx: 1 }} />

          {drawerContent}
        </Drawer>
      )}

      {/* Main content */}
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          bgcolor: "#a5a5a5ff",
          p: 2,
          pt: isMobile ? 9 : 9,
          overflow: "auto",
        }}
      >
        {/* Payment warning modal */}
        <Dialog
          open={paymentModalOpen}
          fullWidth
          fullScreen={fullScreen}
          maxWidth="xs"
          onClose={handleClosePaymentModal}
          aria-labelledby="payment-warning-title"
          PaperProps={{
            sx: {
              borderRadius: fullScreen ? 0 : 2,
              background:
                "linear-gradient(180deg, rgba(20,20,20,0.98), rgba(10,10,10,0.98))",
              color: "#fff",
              mx: fullScreen ? 0 : 2,
            },
          }}
        >
          <DialogTitle id="payment-warning-title" sx={{ color: "#ffdd57", textAlign: "center", fontWeight: 700 }}>
            Payment Plan Required
          </DialogTitle>
          <DialogContent sx={{ pt: 0 }}>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5, px: 0 }}>
              <Typography sx={{ color: "#fff", fontSize: 14, textAlign: "center" }}>
                You still have not created your payment plan. Please contact the administrator to activate your account.
              </Typography>
              <Typography sx={{ color: "#aaa", fontSize: 12, textAlign: "center" }}>
                For security, closing this dialog will sign you out.
              </Typography>
            </Box>
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2, justifyContent: "center" }}>
            <Button
              variant="contained"
              onClick={handleClosePaymentModal}
              sx={{
                background: "linear-gradient(90deg,#d9ae03,#ffdd66)",
                color: "#000",
                fontWeight: 700,
                px: 4,
                borderRadius: 6,
                textTransform: "none",
                "&:hover": { opacity: 0.95 },
              }}
            >
              OK, Logout
            </Button>
          </DialogActions>
        </Dialog>

        {/* Locked account modal (expired payment) */}
        <Dialog
          open={lockedModalOpen}
          fullWidth
          fullScreen={fullScreen}
          maxWidth="xs"
          onClose={handleCloseLockedModal}
          aria-labelledby="locked-warning-title"
          PaperProps={{
            sx: {
              borderRadius: fullScreen ? 0 : 2,
              background:
                "linear-gradient(180deg, rgba(20,10,10,0.98), rgba(10,5,5,0.98))",
              color: "#fff",
              mx: fullScreen ? 0 : 2,
            },
          }}
        >
          <DialogTitle id="locked-warning-title" sx={{ color: "#ff6b6b", textAlign: "center", fontWeight: 800 }}>
            Account Locked
          </DialogTitle>
          <DialogContent sx={{ pt: 0 }}>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5, px: 0 }}>
              <Typography sx={{ color: "#fff", fontSize: 14, textAlign: "center" }}>
                Your account is locked temporarily due to payment delay. Please contact the administrator to restore access.
              </Typography>
              <Typography sx={{ color: "#aaa", fontSize: 12, textAlign: "center" }}>
                You will be logged out when you close this dialog.
              </Typography>
            </Box>
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2, justifyContent: "center" }}>
            <Button
              variant="contained"
              onClick={handleCloseLockedModal}
              sx={{
                background: "linear-gradient(90deg,#ff6b6b,#ff9a9a)",
                color: "#000",
                fontWeight: 700,
                px: 4,
                borderRadius: 6,
                textTransform: "none",
                "&:hover": { opacity: 0.95 },
              }}
            >
              Logout
            </Button>
          </DialogActions>
        </Dialog>

        <Dialogbox
          open={isOpen}
          title={text.navigation.dialogBoxTitle}
          content={text.navigation.dialogBoxMessage}
          agreeButtonText={text.navigation.logout}
          disagreeButtonText={text.navigation.cancel}
          onAgree={handleLogout}
          onDisagree={() => setIsOpen(false)}
          onClose={() => setIsOpen(false)}
        />
        {children}
      </Box>
    </Box>
  );
}

